/**
 * oauthErrors — maps backend OAuth failure reason codes to friendly,
 * user-facing messages.
 *
 * The backend 302-redirects failed OAuth flows to
 * `/login?oauth_error=<reason>`. The raw reason codes are implementation
 * detail (snake_case enum values); never surface them directly. LoginPage
 * reads `?oauth_error` on mount and shows `oauthErrorMessage(reason)` via
 * toast.error(). OAuthCompletePage also routes its own `missing_token`
 * case through this map for consistency.
 *
 * Reason codes (fixed backend contract):
 *   email_unverified | invalid_state | access_denied |
 *   token_exchange_failed | userinfo_failed | account_error |
 *   provider_disabled
 * Plus the frontend-only `missing_token` (OAuthCompletePage redirect when
 * the success fragment is absent).
 */

const OAUTH_ERROR_MESSAGES: Record<string, string> = {
  email_unverified:
    'Your provider account email is not verified. Verify it with the provider, then try again.',
  invalid_state:
    'That sign-in link expired or was already used. Please try signing in again.',
  access_denied: 'Sign-in was cancelled. You can try again whenever you are ready.',
  token_exchange_failed:
    'We could not complete sign-in with your provider. Please try again.',
  userinfo_failed:
    'We could not read your profile from the provider. Please try again.',
  account_error:
    'Something went wrong setting up your account. Please try again or contact support.',
  provider_disabled: 'That sign-in method is not available right now.',
  missing_token: 'Sign-in did not complete. Please try again.',
}

const GENERIC_OAUTH_ERROR =
  'Sign-in could not be completed. Please try again.'

/**
 * Resolve a friendly message for an OAuth error reason code. Unknown or
 * empty codes return a generic message — the raw code is never leaked.
 */
export function oauthErrorMessage(reason: string): string {
  return OAUTH_ERROR_MESSAGES[reason] ?? GENERIC_OAUTH_ERROR
}
